import { type FC, type ReactNode } from 'react';
import { useNavigationHelper } from 'FrontRoyalAngular';
import { type StreamForStreamDashboard } from 'Lessons';
import { type CourseCard } from '../StreamDashboard.types';
import { type useStreamDashboardPage } from '../hooks/useStreamDashboardPage';
import { StreamDashboardMainColumn } from './StreamDashboardMainColumn';
import { StreamDashboardSidebar } from './StreamDashboardSidebar';

// The additionalOffset the Angular scrollHelper call passed.
const KEY_TERMS_SCROLL_OFFSET = 20;

/*
    The two columns, taking the page's view model. Separate from StreamDashboardPage only so that each
    function stays inside the line limits; there is no other reason to split here.
*/

type Props = {
    stream: StreamForStreamDashboard;
    page: ReturnType<typeof useStreamDashboardPage>;
    recommendedCourses: CourseCard[];
    relatedCourses: CourseCard[];
    onOpenCourse: (streamId: string) => void;
    formattedExamOpenTime: string | null;
    certificate: ReactNode;

    // Held back while the mobile route animation runs. See useDeferHeavyContentOnMobile.
    deferHeavyContent: boolean;
    sidebarCertificate: ReactNode;
};

export const StreamDashboardColumns: FC<Props> = ({
    stream,
    page,
    recommendedCourses,
    relatedCourses,
    onOpenCourse,
    formattedExamOpenTime,
    certificate,
    sidebarCertificate,
    deferHeavyContent,
}) => {
    // Use loadUrl, not window.open: under Cordova, loadUrl opens _blank links in the system browser.
    const { loadUrl } = useNavigationHelper();
    const openResourceLink = (url: string) => loadUrl(url, '_blank');

    /*
        scrollHelper.scrollToElement took an extra -20 offset, so the caption stopped 20px short of the
        container top rather than flush against it. scrollIntoView has no offset parameter, hence the
        scrollBy afterwards.
    */
    const scrollToKeyTerms = () => {
        const caption = document.getElementById('key-terms-caption');
        if (!caption) return;

        caption.scrollIntoView({ behavior: 'smooth' });
        caption.closest('.main-box')?.scrollBy({ top: -KEY_TERMS_SCROLL_OFFSET, behavior: 'smooth' });
    };

    return (
        // The 30px gutter applies only side by side. Stacked, the last chapter box's margin supplies the gap.
        <div className="flex flex-col md:flex-row md:gap-7.5">
            <StreamDashboardMainColumn
                stream={stream}
                isSignedIn={!!page.currentUser}
                mustVerifyIdentity={page.mustVerifyIdentity}
                hasExamVerificationRecord={page.hasExamVerificationRecord}
                canLaunchStream={page.canLaunchThisStream}
                canLaunchLesson={page.canLaunchLesson}
                formattedExamOpenTime={formattedExamOpenTime}
                timeLimitHours={page.timeLimitHours}
                showSchedule={page.showSchedule}
                onShowSchedule={page.onShowSchedule}
                summariesForLesson={page.summariesForLesson}
                getLessonAccess={page.getLessonAccess}
                summariesDisabled={page.resourcesDisabled}
                isMobile={page.isMobile}
                deferChapterList={deferHeavyContent}
                onLaunchLesson={page.tryToLaunchLesson}
                onLockedLesson={reasonMessage => {
                    // Desktop anchors the reason to the row as a tooltip, which LessonRow renders from
                    // lockedTooltipMessage. There is nothing to hover on a phone, so it gets a modal.
                    if (page.isMobile) page.setLockedMessage(reasonMessage);
                }}
                onSummaryClick={page.openSummary}
                certificate={certificate}
                shareAndBookmark={{
                    canShare: page.showMainColumnShare,
                    onShare: page.shareStream,
                    showBookmark: page.showMainColumnBookmark,
                    favorite: page.favorite,
                    isBookmarkDisabled: !page.currentUser,
                    onToggleBookmark: page.toggleBookmark,
                }}
            />

            {/* The whole sidebar column carried ng-if="!hidingExtraContentFromMobile" on main. */}
            {!deferHeavyContent && (
                <StreamDashboardSidebar
                    stream={stream}
                    isSignedIn={!!page.currentUser}
                    canShare={page.canShare}
                    showBookmark={page.showBookmark}
                    favorite={page.favorite}
                    onToggleBookmark={page.toggleBookmark}
                    onShare={page.shareStream}
                    formattedExamOpenTime={formattedExamOpenTime}
                    timeLimitHours={page.timeLimitHours}
                    showSchedule={page.showSchedule}
                    onShowSchedule={page.onShowSchedule}
                    recommendedCourses={recommendedCourses}
                    relatedCourses={relatedCourses}
                    onOpenCourse={onOpenCourse}
                    summaries={page.summariesForStream}
                    resourcesDisabled={page.resourcesDisabled}
                    onSummaryClick={page.openSummary}
                    onResourceLinkClick={openResourceLink}
                    keyTermsReady={page.keyTermsReady}
                    hasKeyTerms={page.keyTermCount > 0}
                    onExploreKeyTerms={scrollToKeyTerms}
                    courseTalkId={page.courseTalkId}
                    certificate={sidebarCertificate}
                />
            )}
        </div>
    );
};
